import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Empty,
  Table,
  Button,
  Modal,
  Typography,
  Row,
  Col,
  Image,
  DatePicker,
  Form,
} from "antd";
import {
  LoadingOutlined,
  CalendarOutlined,
  TableOutlined,
  EyeOutlined,
  CameraOutlined,
} from "@ant-design/icons";
import { get_language, translate } from "../../locale";
import moment from "moment";

import { useParams } from "react-router-dom";
import UserServices from "../../services/UserServices";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import SecondaryButton from "../Buttons/SecondaryButton";
import FeedingServices from "../../services/FeedingServices";
import imageRestaurante from "../../assets/images/restaurante.png";
import PrimaryButton from "../Buttons/PrimaryButton";

const localizer = momentLocalizer(moment);

const { Title, Text, Link } = Typography;

const ToolbarCalendar = ({
  handlePrev,
  handleToday,
  handleNext,
  onNavigate,
  label,
}) => (
  <div className="flex flex-row my-4">
    <div>
      <SecondaryButton
        className="mr-4"
        onClick={() => {
          onNavigate("PREV");
          handlePrev();
        }}
      >
        &lt;
      </SecondaryButton>
      <SecondaryButton
        className="mr-4"
        onClick={() => {
          onNavigate("TODAY");
          handleToday();
        }}
      >
        {translate("Hoy")}
      </SecondaryButton>
      <SecondaryButton
        className=""
        onClick={() => {
          onNavigate("NEXT");
          handleNext();
        }}
      >
        &gt;
      </SecondaryButton>
    </div>
    <div className="flex flex-1 flex-row justify-center items-center">
      {label}
    </div>
  </div>
);

ToolbarCalendar.propTypes = {
  onNavigate: PropTypes.func,
  label: PropTypes.string,
  handlePrev: PropTypes.func,
  handleToday: PropTypes.func,
  handleNext: PropTypes.func,
};

const WeekCalendarEvent = ({ event, handleDetails }) => (
  <Link
    className={`text-center w-full block ${
      event.type === "cell" ? "" : "rbc-event-content-header"
    }`}
    style={{ color: "white" }}
    onClick={(e) => {
      e.preventDefault();
      if (event.type === "cell") {
        handleDetails(event);
      }
    }}
  >
    {event.start.toString().indexOf("00:00:00") === -1
      ? moment(event.start).format("HH:mm")
      : event.title}{" "}
    {event.image && <CameraOutlined className="ml-2" />}
  </Link>
);

WeekCalendarEvent.propTypes = {
  event: PropTypes.object,
  handleDetails: PropTypes.func,
};

const FeedingCalendar = ({ feedingSubmodules }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [date, setDate] = useState(
    moment().startOf("isoWeek").format("YYYY-MM-DD")
  );
  const [patient, setPatient] = useState(null);
  const [dataTable, setDataTable] = useState(null);
  const [modalDetails, setModalDetails] = useState(null);
  const [showAs, setShowAs] = useState("calendar");
  const [dateInterval, setDateInterval] = useState({ from: "", to: "" });

  const { username } = useParams();

  useEffect(() => {
    loadPatientInfo();
  }, []);

  useEffect(() => {
    getFeedings();
    const restoreDates = () => {
      if (showAs === "calendar") {
        setDateInterval({ from: "", to: "" });
      }
    };

    restoreDates();
  }, [date, showAs]);

  const loadPatientInfo = async () => {
    try {
      const res = await UserServices.getProfile(username);
      if (res.status >= 200 && res.status < 300) {
        setPatient(res.data.my_contact_info);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getFeedings = async () => {
    setLoading(true);
    try {
      let res = "";

      if (
        showAs === "table" &&
        dateInterval.from !== "" &&
        dateInterval.to !== ""
      ) {
        res = await FeedingServices.getFeeding(
          username,
          dateInterval.from,
          dateInterval.to
        );
      } else {
        res = await FeedingServices.getFeeding(username, date);
      }

      if (res.status >= 200 && res.status < 300) {
        // filtro medicinas tomadas
        let newData = res.data.feedings.map((i) => {
          const newDate =
            i.intake_time.indexOf("00:00:00") === -1
              ? new Date(`${i.intake_date} ${i.intake_time}`)
              : new Date(
                  `${i.intake_date} ${i.intake_time}`.replace(
                    "00:00:00",
                    "00:00:01"
                  )
                );
          return {
            ...i,
            title: i.day_food,
            start: newDate,
            end: newDate,
            allDay: false,
            date_time: `${i.intake_date} ${i.intake_time}`,
            calories: i.food_set.reduce((a, b) => a + b.calories, 0),
            type: "cell",
          };
        });
        // label cantidad de dosis del día

        setDataTable(res.data.feedings);
        let caloriesData = {};
        newData.forEach((i) => {
          const date = moment(i.date_time).format("YYYY-MM-DD");
          const tz = i.date_time.substring(19, 25);
          if (!caloriesData[date]) {
            caloriesData = {
              ...caloriesData,
              [date]: {
                calories: i.calories,
                date_time: date + "T00:00:00" + tz,
              },
            };
          } else {
            const calories =
              parseFloat(caloriesData[date].calories) + parseFloat(i.calories);
            caloriesData[date] = { ...caloriesData[date], calories: calories };
          }
        });

        const caloriesLabels = Object.keys(caloriesData).map((i, k) => {
          return {
            id: "dsg_" + k,
            title: caloriesData[i].calories,
            start: new Date(caloriesData[i].date_time),
            end: new Date(caloriesData[i].date_time),
            allDay: true,
            date_time: caloriesData[i].date_time,
            calories: caloriesData[i].calories,
            type: "header",
          };
        });

        // agregamos los labels
        newData = [...newData, ...caloriesLabels];
        setData(newData);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const antIcon = (
    <LoadingOutlined
      style={{
        fontSize: 24,
      }}
      spin
    />
  );

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex space-x-4 justify-center">
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            className="border-0"
            onClick={() => handleDetails(record)}
          />
        </div>
      ),
    },
    {
      title: "Fecha",
      dataIndex: "intake_date",
      key: "intake_date",
      render: (_, record) =>
        moment(record.intake_date).format(
          get_language === "en" ? "MM/DD/YYYY" : "DD/MM/YYYY"
        ),
      sorter: (a, b) => {
        if (a.intake_date < b.intake_date) return -1;
        if (a.intake_date > b.intake_date) return 1;
        return 0;
      },
    },
    {
      title: "Hora",
      dataIndex: "intake_time",
      key: "intake_time",
      render: (_, record) => record.intake_time,
      sorter: (a, b) => {
        if (a.intake_time < b.intake_time) return -1;
        if (a.intake_time > b.intake_time) return 1;
        return 0;
      },
    },
    {
      title: "Alimento",
      dataIndex: "day_food",
      key: "day_food",
      render: (_, record) =>
        record.day_food !== "undefined" ? record.day_food : translate("N/D"),
      sorter: (a, b) => {
        if (a.day_food < b.day_food) return -1;
        if (a.day_food > b.day_food) return 1;
        return 0;
      },
    },
    {
      title: "Foto",
      dataIndex: "image",
      key: "image",
      render: (_, record) =>
        record.image !== "" ? translate("Si") : translate("No"),
    },
  ];

  const handleHideDetails = () => {
    setModalDetails(null);
  };

  const handlePrev = () => {
    setDate((v) => moment(v).subtract(7, "days").format("YYYY-MM-DD"));
    setDateInterval({ from: "", to: "" });
  };
  const handleToday = () => {
    setDate((v) => moment().startOf("isoWeek").format("YYYY-MM-DD"));
    setDateInterval({ from: "", to: "" });
  };
  const handleNext = () => {
    if (date !== moment().format("YYYY-MM-DD")) {
      setDate((v) => moment(v).add(7, "days").format("YYYY-MM-DD"));
      setDateInterval({ from: "", to: "" });
    }
  };
  const handleDetails = (item) => {
    setModalDetails(item);
  };

  const handleDateIntervalChange = (date, value) => {
    setDateInterval((prevValues) => ({
      ...prevValues,
      [value]: moment(date).format("YYYY-MM-DD"),
    }));
  };

  return (
    <Row className="w-full">
      <Col flex="column" className="w-full" id="Feeding">
        <Row className="w-full">
          <Col span={24} className="mt-4">
            <Title level={2} className="text-center">
              {translate("Comidas de ")}{" "}
              {patient && `${patient.user.full_name}`}
            </Title>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col span={24} className="flex flex-row justify-end space-x-2">
            <span className="flex flex-col justify-center">
              {translate("Formato")}:
            </span>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => setShowAs("calendar")}
              type={showAs === "calendar" ? "primary" : "default"}
            />
            <Button
              icon={<TableOutlined />}
              onClick={() => setShowAs("table")}
              type={showAs === "table" ? "primary" : "default"}
            />
          </Col>
        </Row>
        {showAs === "calendar" && (
          <Calendar
            localizer={localizer}
            defaultDate={null}
            defaultView={Views.WEEK}
            views={["week"]}
            events={data || []}
            startAccessor="start"
            endAccessor="end"
            step={60}
            timeslots={6}
            style={{ height: 500 }}
            components={{
              toolbar: (props) =>
                ToolbarCalendar({
                  ...props,
                  handlePrev,
                  handleToday,
                  handleNext,
                }),
              week: {
                event: ({ event }) =>
                  WeekCalendarEvent({ event, handleDetails }),
              },
            }}
            formats={{
              eventTimeRangeFormat: () => {
                return "";
              },
              timeGutterFormat: (date, culture, localizer) => {
                const time = localizer.format(date, "HH:mm", culture);
                let rs = "";
                switch (time) {
                  case "00:00":
                    rs = "";
                    break;
                  case "06:00":
                    rs = "";
                    break;
                  case "12:00":
                    rs = "";
                    break;
                  case "18:00":
                    rs = "";
                    break;
                }
                return rs;
              },
            }}
          />
        )}
        {showAs === "table" && (
          <>
            <div className="flex flex-row my-4">
              <div>
                <SecondaryButton
                  className="mr-4"
                  onClick={() => {
                    handlePrev();
                  }}
                >
                  &lt;
                </SecondaryButton>
                <SecondaryButton
                  className="mr-4"
                  onClick={() => {
                    handleToday();
                  }}
                >
                  {translate("Hoy")}
                </SecondaryButton>
                <SecondaryButton
                  className=""
                  onClick={() => {
                    handleNext();
                  }}
                >
                  &gt;
                </SecondaryButton>
              </div>
            </div>
            <div className="flex flex-col">
              <Form.Item label={translate("Desde")} name="dateFrom">
                <DatePicker
                  name="DatePickerFrom"
                  onChange={(date) => handleDateIntervalChange(date, "from")}
                  format={"YYYY/MM/DD"}
                  value={dateInterval.from ? moment(dateInterval.from) : null}
                  allowClear={false}
                />
              </Form.Item>

              <Form.Item label={translate("Hasta")} name="dateTo">
                <DatePicker
                  onChange={(date) => handleDateIntervalChange(date, "to")}
                  format={"YYYY/MM/DD"}
                  value={dateInterval.to ? moment(dateInterval.to) : null}
                  allowClear={false}
                />
              </Form.Item>

              <PrimaryButton
                className="w-20 mt-4 text-center"
                disabled={dateInterval.from === "" || dateInterval.to === ""}
                onClick={() => {
                  getFeedings();
                }}
              >
                {translate("Buscar")}
              </PrimaryButton>
            </div>
            <Table
              rowKey={(i) => i.id}
              className="w-full mt-6"
              dataSource={dataTable}
              columns={columns}
              loading={
                loading
                  ? { indicator: antIcon, className: "text-custom_blue" }
                  : false
              }
              locale={{
                emptyText: <Empty description={translate("No hay datos")} />,
                sortTitle: translate("Ordenar"),
                triggerDesc: translate("Ordenar (descendente)"),
                triggerAsc: translate("Ordenar (ascendente)"),
                cancelSort: translate("Cancelar orden"),
                filterTitle: translate("Filtros"),
                filterConfirm: translate("Aceptar"),
                filterReset: translate("Limpiar"),
                filterEmptyText: translate("No hay filtros"),
                filterCheckall: translate("Seleccinar todos"),
                filterSearchPlaceholder: translate("Buscar en filtros"),
              }}
            />
          </>
        )}
        <Modal
          width={600}
          open={modalDetails !== null}
          title={translate("Detalles del alimento")}
          onCancel={handleHideDetails}
          footer={[
            <Button key="ok" type="primary" onClick={handleHideDetails}>
              {translate("Aceptar")}
            </Button>,
          ]}
        >
          {modalDetails !== null && (
            <Row>
              <Col className="flex-1">
                <Row>
                  <Col>
                    <Text>
                      {moment(
                        `${modalDetails.intake_date} ${modalDetails.intake_time}`
                      )
                        .locale(get_language)
                        .format("dddd L HH:mm")}{" "}
                      Horas
                    </Text>
                  </Col>
                </Row>
                <Row className="mt-4 border-0 border-solid border-b border-b-black/20 pb-4 mr-6">
                  <Col>
                    <Text>
                      {translate("Alimento")}:{" "}
                      <span className="text-gray-500">
                        {modalDetails.day_food !== "undefined"
                          ? modalDetails.day_food
                          : translate("N/D")}
                      </span>
                    </Text>
                  </Col>
                </Row>
                <Row className="pb-2 border-0 border-solid border-b border-b-black/20 mr-6">
                  <Col span={14}>
                    {modalDetails.food_set.map((fs) => (
                      <div key={fs.id} className=" pb-2">
                        <Row gutter={[4, 4]} className="mt-4 mb-1">
                          <Col>
                            <Image preview={false} src={imageRestaurante} />
                          </Col>
                          <Col>
                            <Text>{fs.descriptive_name}</Text>
                          </Col>
                        </Row>
                        <Row gutter={[24, 4]}>
                          {feedingSubmodules?.find(
                            (module) =>
                              module.label === "Calorias" && module.selected
                          ) && (
                            <Col>
                              <Text>{fs.calories} kcal</Text>
                            </Col>
                          )}

                          {feedingSubmodules?.find(
                            (module) =>
                              module.label === "Carbohidratos" &&
                              module.selected
                          ) && (
                            <Col>
                              <Text>
                                {fs.carbohydrate} {translate("Carbohidratos")}
                              </Text>
                            </Col>
                          )}

                          {feedingSubmodules?.find(
                            (module) =>
                              module.label === "Creditos" && module.selected
                          ) && (
                            <Col>
                              <Text>
                                {fs.credits} {translate("Créditos")}
                              </Text>
                            </Col>
                          )}
                        </Row>
                      </div>
                    ))}
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col>
                    <Text>
                      {translate("Estado de ánimo")}:{" "}
                      <span className="text-gray-500">
                        {modalDetails.mood !== "undefined"
                          ? modalDetails.mood
                          : translate("N/D")}
                      </span>
                    </Text>
                  </Col>
                </Row>
                <Row className="mt-1">
                  <Col>
                    <Text>
                      {translate("Nivel de apetito")}:{" "}
                      <span className="text-gray-500">
                        {modalDetails.appetite_level !== "undefined"
                          ? modalDetails.appetite_level
                          : translate("N/D")}
                      </span>
                    </Text>
                  </Col>
                </Row>
              </Col>

              <Col span={10}>
                {modalDetails.image_url && (
                  <Image src={modalDetails.image_url} />
                )}
              </Col>
            </Row>
          )}
        </Modal>
      </Col>
    </Row>
  );
};

FeedingCalendar.propTypes = {
  feedingSubmodules: PropTypes.array,
};

export default FeedingCalendar;
